/**
 * The one formatter. Every number a client reads is turned into text here and nowhere else.
 *
 * That is not tidiness: QA gate G2 requires every number in the PDF to equal its value in the JSON
 * sidecar, and the only way to check that mechanically is for both to have been produced by the
 * same function (GA-20). Rules: REPORT_SPEC "Copy rules" — A$45,000 not 45k, ranges as
 * "25–60 hours/month", sentence case, every estimate labelled with its confidence word.
 */

/** DESIGN_BRIEF and WEBSITE_SPEC §"The four marks" — the confidence system, in four words. */
export type ConfidenceMark = "inferred" | "stated" | "verified" | "indication";

/** SCORING_SPEC §6 — an indication's range is rounded to the nearest A$5,000. */
export const INDICATION_ROUNDING_AUD = 5_000;

const EN_DASH = "–";

function group(n: number): string {
  return Math.abs(n).toLocaleString("en-AU", { maximumFractionDigits: 0 });
}

/** A$45,000. Negative money reads as −A$4,000, never as A$-4,000. */
export function aud(value: number): string {
  const rounded = Math.round(value);
  return rounded < 0 ? `−A$${group(rounded)}` : `A$${group(rounded)}`;
}

/**
 * Money in another wave-1 market, from the minor unit a listing stores (`asking_price_minor`,
 * `currency`). The symbol is the market's own — £ and C$ are not "A$" with a different code — and
 * the grouping is the same, so a page with three markets on it reads as one system (ADR-018).
 */
export const CURRENCY_SYMBOL: Record<string, string> = { AUD: "A$", GBP: "£", CAD: "C$", USD: "US$", EUR: "€" };
export function moneyMinor(minor: number, currency: string): string {
  const symbol = CURRENCY_SYMBOL[currency];
  if (symbol === undefined) throw new Error(`no symbol for currency ${currency}; add it to CURRENCY_SYMBOL when the market opens`);
  const major = Math.round(minor / 100);
  return major < 0 ? `−${symbol}${group(major)}` : `${symbol}${group(major)}`;
}

/** "A$35,000–A$45,000". Both ends carry the unit, because one end alone gets quoted. */
export function audRange(low: number, high: number): string {
  return `${aud(low)}${EN_DASH}${aud(high)}`;
}

/** Round to a step, for the indication's A$5,000 range (SCORING_SPEC §6). */
export function roundAud(value: number, step: number): number {
  return Math.round(value / step) * step;
}

/** "25–60 hours/month", "1 hour/month" — the unit REPORT_SPEC page 4 prints. */
export function hoursPerMonth(low: number, high: number): string {
  const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
  const unit = low === high && low === 1 ? "hour/month" : "hours/month";
  return low === high ? `${fmt(low)} ${unit}` : `${fmt(low)}${EN_DASH}${fmt(high)} ${unit}`;
}

/** "3–8 months", "4 months", "under a month". Payback is printed in whole months. */
export function months(low: number, high = low): string {
  const lo = Math.round(low);
  const hi = Math.round(high);
  if (hi < 1) return "under a month";
  if (lo === hi) return `${lo} ${lo === 1 ? "month" : "months"}`;
  return `${Math.max(lo, 0)}${EN_DASH}${hi} months`;
}

/** "12%" — one decimal only where it changes the figure a reader would check. */
export function percent(ratioOrPct: number, { isRatio = false } = {}): string {
  const pct = isRatio ? ratioOrPct * 100 : ratioOrPct;
  const rounded = Math.round(pct * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
}

/** "2.8×" — the sector multiple, with the multiplication sign, not the letter x. */
export function multiple(value: number): string {
  return `${Math.round(value * 10) / 10}×`;
}

/**
 * "13 September 2026". Spelled out because 9/13 and 13/9 mean different things on either side of
 * the Pacific and this report is read in five markets.
 */
export function longDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) throw new Error(`longDate expects an ISO date, got ${iso}`);
  const MONTHS = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

/** Data confidence 1–3 to the mark the design shows it with. */
export function confidenceMark(confidence: 1 | 2 | 3, isIndication = false): ConfidenceMark {
  if (isIndication) return "indication";
  return confidence === 3 ? "verified" : confidence === 2 ? "stated" : "inferred";
}

/** Difficulty as a word — REPORT_SPEC page 4 prints the word, never an icon or a score. */
export function difficultyWord(difficulty: "easy" | "medium" | "hard"): string {
  return difficulty;
}
